import type { AuctionEventStatus, StatusSeverity } from '@neon-one/auctions-types';
import { AuctionEventStatusSchema } from '@neon-one/auctions-types';
import type { PrototypeAuctionStatus } from '../prototype-shim';
import { parseAuctionDateMs } from '../prototype-shim';

export const AuctionStatusDot = { Steady: 'steady', Pulse: 'pulse' } as const;
export type AuctionStatusTag = {
    readonly label: string;
    readonly severity: StatusSeverity | 'contrast';
} & (
    { readonly icon: string; readonly dot?: never }
    | { readonly icon?: never; readonly dot: (typeof AuctionStatusDot)[keyof typeof AuctionStatusDot] }
);

const AuctionStatus = AuctionEventStatusSchema.enum;
const HALF_DAY_HOURS = 12;
const MINUTE_DIGITS = 2;
const SCHEDULED_MONTH_FORMAT = { month: 'short' } as const;

// per Studio 0008 and 0016: running states use a dot, finished states keep their glyph.
export const AUCTION_STATUS_MAP: Readonly<Record<AuctionEventStatus, AuctionStatusTag>> = {
    [AuctionStatus.draft]: { label: 'Draft', severity: 'secondary', icon: 'pi pi-minus' },
    [AuctionStatus.published]: { label: 'Scheduled', severity: 'info', icon: 'pi pi-calendar' },
    [AuctionStatus.open]: { label: 'Live', severity: 'success', dot: AuctionStatusDot.Pulse },
    [AuctionStatus.closed]: { label: 'Closed', severity: 'secondary', icon: 'pi pi-check' },
} as const satisfies Record<AuctionEventStatus, AuctionStatusTag>;

// Paused modifies the live auction reading without adding a wire status.
export const AUCTION_PAUSED_TAG: AuctionStatusTag = {
    label: 'Paused', severity: 'warn', icon: 'pi pi-pause',
};

const PROTOTYPE_WIRE_STATUS = {
    draft: AuctionStatus.draft,
    scheduled: AuctionStatus.published,
    live: AuctionStatus.open,
    closed: AuctionStatus.closed,
} as const satisfies Record<PrototypeAuctionStatus, AuctionEventStatus>;

export function auctionStatusTag(status: AuctionEventStatus | PrototypeAuctionStatus): AuctionStatusTag {
    const wireStatus = status in PROTOTYPE_WIRE_STATUS
        ? PROTOTYPE_WIRE_STATUS[status as PrototypeAuctionStatus]
        : status as AuctionEventStatus;
    return AUCTION_STATUS_MAP[wireStatus];
}

export function scheduledAuctionTag(startAt: string | null | undefined): AuctionStatusTag {
    const entry = AUCTION_STATUS_MAP[AuctionStatus.published];
    if (!startAt) return entry;
    const timestamp = parseAuctionDateMs(startAt);
    if (!Number.isFinite(timestamp)) return { ...entry, label: `${entry.label} for ${startAt}` };
    const date = new Date(timestamp);
    const month = date.toLocaleString('en-US', SCHEDULED_MONTH_FORMAT);
    const hour = date.getHours();
    const displayHour = hour % HALF_DAY_HOURS || HALF_DAY_HOURS;
    const minute = String(date.getMinutes()).padStart(MINUTE_DIGITS, '0');
    const halfDay = hour >= HALF_DAY_HOURS ? 'PM' : 'AM';
    return { ...entry, label: `${entry.label} for ${month} ${date.getDate()} at ${displayHour}:${minute} ${halfDay}` };
}
